import { Component, OnInit, AfterContentInit, ViewChild, ViewContainerRef, ComponentFactoryResolver, HostListener } from '@angular/core'
import { CtrlBaseComponent } from '../ctrl-base.component';
import { CtStepperItemComponent } from "../ct-stepper-item/ct-stepper-item.component";
import { ADN_PLUS } from '../../models';
import { CtProgressBarComponent } from '../ct-progress-bar/ct-progress-bar.component';
import { PfComponents } from '../../enums/pfComponents';
import { StepperItemTypes, StepperVersion } from '../../enums/stepper';
import { breakpoints } from '../../environments/environment.breakpoints';

@Component({
  selector: 'ct-stepper',
  templateUrl: './ct-stepper.component.html',
  styleUrls: ['./ct-stepper.component.css'],
})

/*The CtStepperComponent is an Angular component that is used to create a stepper wizard. */
export class CtStepperComponent extends CtrlBaseComponent implements OnInit {
  @ViewChild('stepperItemsTarget', { read: ViewContainerRef }) targetViews: any;
  @ViewChild('target', { read: ViewContainerRef }) targetChilds: any;
  @ViewChild('progressBarContainer', { read: ViewContainerRef })
  progressBarTarget: any;

  public selectedStepperItem: any;
  public stepperItems: any = [];
  public context: ADN_PLUS;
  public stepperInstace: any;
  public attempts: number = 0;
  public firstStepView: any;
  public saveState: any;
  public autoNextStep: boolean = true;

  //v4 Properties
  private stepperVersion: StepperVersion;

  //new stepper compressed
  private width: number = 350;
  private height: number = 450;
  private hasProgressBar: boolean = true;
  private ctProgressBarComponent: CtProgressBarComponent;

  //stepper full
  private isStepperFullFixed : boolean = true;
  public isExtraSmall : boolean = false;
  public fullStepperHasHeader : boolean = true;

  //types
  public stepperItemTypesEnum = StepperItemTypes;

  //functions
  @HostListener('window:resize', ['$event'])
  onResize() {
    if(window.innerWidth >= breakpoints.S && window.innerWidth <= breakpoints.M){
      this.isExtraSmall = true;
    } else {
      this.isExtraSmall = false;
    }
  }

  constructor() {
    super();
    this.onResize();
  }


  ngOnInit(): void {
    this.setProperties(this);
    if (!this.getMetaData()) {
      this.setMetaData({
        id: 'stpStepper',
        ctType: 'CtStepper',
        controlProperties: {
          hidden: false,
          parentId: '',
        },
        controlActions: {},
        encodedJson: false,
      });
      this.context = new ADN_PLUS();
    }
  }

  /**
   * Sets the stepperItem type to every model in the metadata.
   * Paints the children of the stepper(stepper items).
   * Sets the progress bar if the stepper version is compressed and the progress bar is enabled.
   */
  ngAfterViewInit(): void {
    this.setStepperItemsData();
    if (this.stepperVersion === StepperVersion.V3) {
      this.setStepperItems(this.getMetaData().models);
      if (!this.getSelectedStepperItem()) {
        this.setSelectedStepperItem(
          this.getRealStepperId(this.stepperItems[0].controlProperties.id)
        );
      }
      this.childElements = this.paintService.paintChildren(
        this.getMetaData(),
        this.compiler,
        this.targetViews
      );
    } else {
      this.childElements = this.paintService.paintChildren(
        this.getMetaData(),
        this.compiler,
        this.targetChilds
      );
      this.stepperItems = this.childElements;
      this.changeStep(0);
    }
    this.setProgressBar();
  }

  /**
   * This function sets the stepper version for each stepper item model in the metadata.
   */
  public setStepperItemsData(): void {
    this.getMetaData().models.forEach((stepperItemModel: any) => {
      stepperItemModel.controlProperties['stepperVersion'] = this.getStepperVersion();
      if(this.getStepperVersion() === StepperVersion.Full){
        stepperItemModel.controlProperties['isStepperFullFixed'] = this.isStepperFullFixed;
      }
    });
  }

  /**
   * Sets up the progress bar. 
   * It first checks if the stepper version is compressed and if the progress bar is enabled. 
   * If both conditions are met, it creates metadata for the progress bar, 
   * Finally updates the progress bar's value.
   */
  private setProgressBar(): void {
    if (this.stepperVersion !== StepperVersion.Compressed) return;
    if(!this.getHasProgressBar()) return;
    const progressBarMetaData: any = this.createProgressBarMetadata();
    this.paintChildren(
      { models: [progressBarMetaData] },
      this.progressBarTarget
    );
    this.setCtProgressBarComponent();
    this.changeProgressBarValue();
  }

  /**
   * This function creates metadata for the progress bar.
   */
  private createProgressBarMetadata(): any {
    const progressMetadata: any = {
      ctType: PfComponents.CtProgressBar,
      controlProperties: {
        hidden: 0,
        listenedComponentId: '',
        listenedProperty: '',
        progressPercentage: 1,
        id: `${this.getViewId()}_${PfComponents.CtProgressBar}_Default`,
        modelId: '',
      },
      controlActions: [],
      models: [],
    };
    this.getMetaData().models.push(progressMetadata);
    return progressMetadata;
  }

  /**
   * This function sets the ctProgressBarComponent property to the component instance of the progress bar 
   * by iterating through the child elements and finding the one with a ctType of PfComponents.CtProgressBar.
   */
  private setCtProgressBarComponent(): void {
    this.childElements.forEach((child: any) => {
      if (child.ctType === PfComponents.CtProgressBar) {
        this.ctProgressBarComponent = child;
      }
    });
  }

  /**
   * This function updates the progress bar's value based on the current step in the stepper. 
   * It first checks if the ctProgressBarComponent is set, and if so,
   *  it calculates the percentage of steps completed and sets the progress bar's value using setProgressPercentage().
   */
  public changeProgressBarValue(): void {
    if (this.ctProgressBarComponent) {
      let currentPorcentage: number =
        ((this.getCurrentIndex() + 1) /
          this.childElements.filter(
            (child: CtrlBaseComponent) =>
              child.getCtType() === PfComponents.CtStepperItem
          ).length) *
        100;
      this.ctProgressBarComponent.setProgressPercentage(currentPorcentage);
    }
  }

  public goToStep(index: number) {
    let current = this.stepperItems.indexOf(this.getSelectedStepperItem());
    if (current == index) return;
    if (current < index) {
      this.nextStep();
    } else {
      this.prevStep();
    }
  }

  public changeStep(index: number) {
    let target: CtStepperItemComponent = this.stepperItems[index];
    if (this.selectedStepperItem === target.getId()) return;

    if (target.getReadOnly()) {
      if (index != this.getStepperItems().length - 1)

        this.plusAlertService.setCtDialogInfo('ATENCIÓN', 'downloadError', 'downloadError', 'primary', target.getDisabledMessage(), true, 'De acuerdo', 'Cancelar');

      return;
    }

    let actual: any = this.getSelectedStepperItem();

    for (let vStepperItem of this.childElements) {
      if (vStepperItem['ctType'] !== PfComponents.CtStepperItem) continue;
      let step = <CtStepperItemComponent>vStepperItem;
      if (target.getId() === step.getId() && !step.getReadOnly()) {
        this.loadValues(step);
        if (step.getLoadOnce()) {
          step.setLoaded(true);
        }

        vStepperItem.event2('onActivation');
        step.setActive(true);
        step.setState('current');
        step.setHidden(false);
        if (actual && this.stepperItems.indexOf(actual) < index) {
          actual.setState('done');
        }
      } else {
        step.setActive(false);
        step.setHidden(true);
      }
    }

    if (
      this.getSelectedStepperItem &&
      this.stepperVersion === StepperVersion.V3
    ) {
      this.utilService
        .getComponentRef(this.getSelectedStepperItem())
        .bindContext();
    }
    this.setSelectedStepperItem(target);
    this.changeProgressBarValue();
  }

  public prevStep() {
    this.event2('onPrevStep');
    let stepper: CtStepperItemComponent = this.getSelectedStepperItem();
    stepper.onPrevButton();
  }

  public nextStep() {
    let current = this.getCurrentIndex();

    if (current == this.getStepperItems().length - 2) {
      this.event2('onGoToFinish');
    }
    this.event2('onNextStep');
    let stepper: CtStepperItemComponent = this.getSelectedStepperItem();
    stepper.onNextButton();
  }

  public changeNextState(value = false) {
    let current = this.stepperItems.indexOf(this.getSelectedStepperItem());
    if (current >= 0 && current < this.stepperItems.length - 1) {
      const nextStep = this.stepperItems[current + 1];
      nextStep.setReadOnly(value);
    }
  }

  getCurrentIndex() {
    return this.stepperItems.indexOf(this.getSelectedStepperItem());
  }

  loadSaveState() {
    if (this.getSelectedStepperItem())
      this.stepperInstace.goTo(
        this.getSteppIndex(this.getSelectedStepperItem()) + 1
      );
  }

  getRealStepperId(id) {
    if (!id) return;
    let vViewRegex = new RegExp('^.{2}\\_VI[0-9]{1,5}');
    if (!vViewRegex.test(id)) {
      return (id = this.getViewId() + '_' + id);
    } else {
      return id;
    }
  }

  saveStepperState(step: any) {
    if (!step) return;
    if (typeof step == 'string') {
      step = this.getStepperInstace(step)[0];
    }
    let viewController = this.utilService.getComponentRef(step.getViewCode());
    viewController.setMetaData(
      this.utilService.extractMetadata(
        viewController.getMetaData(),
        viewController
      )
    );
    this.utilService.checkStack(viewController);

    viewController = this.utilService.getComponentRef(this.getViewId());
    viewController.setMetaData(
      this.utilService.extractMetadata(
        viewController.getMetaData(),
        viewController
      )
    );
    this.utilService.checkStack(viewController);
  }

  loadValues(pstepper: any) {
    if (typeof pstepper == 'string') {
      pstepper = this.getStepperInstace(pstepper)[0];
    }
    if (this.attempts > 5) return;
    if (
      pstepper.getGetValueFrom() &&
      pstepper.getSetValueTo() &&
      !pstepper.loaded
    ) {
      if (
        !this.utilService.getComponentRef(pstepper.getGetValueFrom()) ||
        !this.utilService.getComponentRef(pstepper.getSetValueTo())
      ) {
        this.attempts += 0.5;
        setTimeout(() => {
          this.loadValues(pstepper);
        }, this.attempts * 1000);
      }
      let adn = this.utilService.getAdnFromAnyComponent(
        pstepper.getGetValueFrom()
      );
      this.utilService.setAdnToAnyComponent(pstepper.getSetValueTo(), adn);
    }
  }

  getStepperInstace(id: any) {
    let result = null;
    result = this.childElements.filter((si: any) => {
      if (si.getId() == id) return si;
    });
    return result;
  }

  getSteppIndex(id: any): number {
    for (let index = 0; index < this.childElements.length; index++) {
      if (this.childElements[index].getId() == id) {
        return index;
      }
    }
    return -1;
  }

  canNext(): boolean {
    if (!this.getSelectedStepperItem()) return;
    let nextIndex =
      this.getSteppIndex(this.getSelectedStepperItem().getId()) + 1;
    let step = this.stepperItems[nextIndex];
    return step.getReadOnly() || false;
  }

  getIconClass(step: any) {
    if (!step) return '';
    let res = step.getState() != 'done' ? step.getIcon() : 'fa fa-check-circle';
    return res;
  }

  finishStep() {
    this.event2('onFinish');
  }

  public isStepperFullItemActive(index : number): boolean {
    if(this.getCurrentIndex() === index) return true;
    return false;
  }

  public isStepperFullItemCompleted(index : number): boolean {
    if(this.getCurrentIndex() > index) return true;
    return false;
  }

  public get stepperType(): typeof StepperVersion {
    return StepperVersion;
  }

  public getStepperItemsStepperFull(): any[] {
    let result : any[] = [];
    this.getStepperItems().forEach((stepperItem : any) => {
      if(stepperItem.stepperItemType !== StepperItemTypes.confirmation) result.push(stepperItem);
    });
    return result;
  }

  getStepperItems() { return this.stepperItems; }
  getSaveState() { return this.saveState; }
  getSelectedStepperItem() { return this.selectedStepperItem; }
  getStepperVersion() { return this.stepperVersion; }
  getAutoNextStep() { return this.autoNextStep; }
  getWidth(): number { return this.width; }
  getHeight(): number { return this.height; }
  getHasProgressBar(): boolean { return this.hasProgressBar; }
  getIsStepperFullFixed(): boolean { return this.isStepperFullFixed; }
  getFullStepperHasHeader(): boolean { return this.fullStepperHasHeader }

  setStepperItems(pStepperItems: any[] = null): void {
    for (let vStepperItem of pStepperItems) {
      if (vStepperItem.controlProperties.ctType !== PfComponents.CtStepperItem)
        continue;
      let newStepperItem = {};
      newStepperItem = {
        ...newStepperItem,
        controlActions: vStepperItem.controlActions,
        controlProperties: vStepperItem.controlProperties,
      };
      this.stepperItems = [...this.stepperItems, newStepperItem];

      if (vStepperItem.controlProperties.currentItem)
        this.setSelectedStepperItem(vStepperItem.controlProperties.id);
    }
  }

  setSelectedStepperItem(step: string | CtStepperItemComponent): void {
    if (!step && this.childElements.length > 0)
      this.setSelectedStepperItem(
        <CtStepperItemComponent>this.childElements[0]
      );
    else this.selectedStepperItem = step;
    this.event2('onTabChange');
  }

  setSaveState(pValue: any) {  this.saveState = pValue; }
  setAutoNextStep(autoNextStep: any) { this.autoNextStep = autoNextStep; }
  setStepperVersion(stepperVersion: StepperVersion) { this.stepperVersion = stepperVersion; }
  setWidth(width: number): void { this.width = width; }
  setHeight(height: number): void { this.height = height; }
  setHasProgressBar(pHasProgressBar: boolean) { this.hasProgressBar = pHasProgressBar; }
  setIsStepperFullFixed(pIsStepperFullFixed: boolean) { this.isStepperFullFixed = pIsStepperFullFixed; }
  setFullStepperHasHeader(pFullStepperHasHeader : boolean) { this.fullStepperHasHeader = pFullStepperHasHeader; }
}